import React, { useState, useEffect, useRef } from "react";
import { searchProducts } from "../../../../services/pos-api";
import { handleFormattedValidationErrors } from "../../../../utils/errorHandlers";
import BarcodeIcon from "../../../../assets/barcode-icon.svg";
import InvalidBarcodeModal from "./InvalidBarcodeModal";
import { useAppSelector } from "../../../../store/hooks";
const SearchHeader = ({
  onSearchResults,
  cartItemCount,
  onShowCart,
  currentCategory,
  currentSubCategory,
  onClearSearch,
  onAutoAddToCart,
  clearSearchTrigger,
}) => {
  const [showInvalidBarcodeModal, setShowInvalidBarcodeModal] = useState(false);
  const [searchValue, setSearchValue] = useState("");
  const [isSearching, setIsSearching] = useState(false);
  const searchInputRef = useRef(null);
  const { isPartsURL, isAccessoriesURL } = useAppSelector(
    (state) => state.domain
  );
  // Auto-focus search input when component mounts
  useEffect(() => {
    if (searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, []);

  // Clear search value when clearSearchTrigger changes
  useEffect(() => {
    if (clearSearchTrigger > 0) {
      setSearchValue("");
    }
  }, [clearSearchTrigger]);

  // Clear search results when search value is empty
  useEffect(() => {
    if (searchValue.trim() === "") {
      if (onClearSearch) {
        onClearSearch();
      }
      // Refocus search input when search is cleared
      setTimeout(() => {
        if (searchInputRef.current) {
          searchInputRef.current.focus();
        }
      }, 100);
    }
  }, [searchValue, onClearSearch]);

  const getCurrentSearchValue = () => {
    if (searchInputRef.current) {
      return searchInputRef.current.value;
    }
    return searchValue;
  };

  const performSearch = async (inputValue, page = 1) => {
    const currentValue = (inputValue ?? getCurrentSearchValue() ?? "").trim();

    if (currentValue === "" || currentValue.length < 2) {
      return;
    }

    try {
      setIsSearching(true);
      const categoryId = currentCategory?.id || null;
      const brandId = currentSubCategory?.id || null;
      const response = await searchProducts(
        currentValue,
        categoryId,
        brandId,
        null,
        page
      );

      if (response.results && response.results.length > 0) {
        const productWithUniqueId = response.results.find(
          (product) =>
            product.unique_id !== null && product.unique_id !== undefined
        );

        if (productWithUniqueId && onAutoAddToCart && page === 1) {
          onAutoAddToCart(productWithUniqueId);
          setSearchValue("");
          setTimeout(() => {
            if (searchInputRef.current) {
              searchInputRef.current.focus();
            }
          }, 100);
          return;
        }

        if (onSearchResults) {
          onSearchResults(response.results, currentValue, page, response.next);
        }
      } else if (page === 1) {
        setShowInvalidBarcodeModal(true);
      }
    } catch (error) {
      console.error("Search error:", error);
      if (page === 1) setShowInvalidBarcodeModal(true);
      handleFormattedValidationErrors(error, "Search failed");
    } finally {
      setIsSearching(false);
    }
  };

  const handleSearch = async () => {
    // Manual search trigger (for barcode icon click)
    await performSearch(getCurrentSearchValue());
  };

  const closeModal = () => {
    setShowInvalidBarcodeModal(false);
    // Refocus search input after closing modal
    if (searchInputRef.current) {
      searchInputRef.current.focus();
    }
  };

  const handleSearchInputChange = (e) => {
    const value = e.target.value;
    setSearchValue(value);
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      performSearch(getCurrentSearchValue());
    }
  };

  const handleSearchContainerClick = () => {
    // Focus search input when clicking anywhere in the search container
    if (searchInputRef.current) {
      searchInputRef.current.focus();
    }
  };

  return (
    <div className="pos-header-container">
      <div className="pos-header">
        <h1 className="pos-title">POS</h1>
        <div
          className={
            isPartsURL
              ? "pos-search-container-parts"
              : isAccessoriesURL
              ? "pos-search-container-acc"
              : "pos-search-container"
          }
          onClick={handleSearchContainerClick}
        >
          <input
            ref={searchInputRef}
            type="text"
            placeholder="Scan/Enter barcode or search by products"
            className="pos-search-input"
            value={searchValue}
            onChange={handleSearchInputChange}
            onKeyDown={handleKeyDown}
            disabled={isSearching}
          />
          <div
            className={`barcode-container ${isSearching ? "searching" : ""}`}
          >
            <img
              src={BarcodeIcon}
              alt="Barcode scanner"
              className="barcode-icon"
              onClick={handleSearch}
              style={{ cursor: isSearching ? "not-allowed" : "pointer" }}
            />
            {isSearching && <div className="search-spinner"></div>}
          </div>
        </div>
      </div>

      <InvalidBarcodeModal
        isOpen={showInvalidBarcodeModal}
        onClose={closeModal}
      />
    </div>
  );
};

export default SearchHeader;
